import { Controller } from "@hotwired/stimulus";

// Contrôleur métier "invest-details" (réduit) :
// - conserve les actions spécifiques comme le choix d'un programme fiscal
// - ne gère plus les requêtes ni le rendu (délégués à api-sync et remote-bind)
export default class extends Controller {
  static targets = ["comparatorRow"];

  connect() {
    this.onSimulationTaxProgramChange = this.onSimulationTaxProgramChange.bind(this);
    this.element.addEventListener("change", this.onSimulationTaxProgramChange);
    // Ordre initial : lire la fiscalité active dans le formulaire (select PTZ ou investissement).
    this.moveComparatorCardFirst(this.currentTaxProgramValue());
  }

  disconnect() {
    this.element.removeEventListener("change", this.onSimulationTaxProgramChange);
  }

  /**
   * Réordonne les cartes du comparateur quand la fiscalité change via le formulaire
   * (select « Fiscalité », « Bénéficiaire du PTZ », ou bascule Résidence principale /
   * Investissement), pas au clic sur une carte du comparateur.
   */
  onSimulationTaxProgramChange(event) {
    const el = event.target;
    if (!this.isTaxProgramControl(el)) return;

    this.moveComparatorCardFirst(this.currentTaxProgramValue(el));
  }

  moveComparatorCardFirst(taxProgram) {
    if (!this.hasComparatorRowTarget) return;
    const row = this.comparatorRowTarget;
    const normalized = this.normalizeTaxProgramForComparator(taxProgram);
    if (!normalized) return;
    const card = row.querySelector(`[data-tax-program="${normalized}"]`);
    if (!card || card === row.firstElementChild) return;
    row.insertBefore(card, row.firstElementChild);
  }

  normalizeTaxProgramForComparator(value) {
    const v = (value || "").toString().trim();
    if (!v) return null;
    if (v === "bailleur_prive") return "jeanbrun";
    return v;
  }

  isTaxProgramControl(el) {
    if (!(el instanceof HTMLSelectElement || el instanceof HTMLInputElement)) return false;
    if (el.dataset.toggleScope !== "tax_program") return false;
    return (el.dataset.target || "").split(/\s+/).includes("toggle.select");
  }

  /**
   * Valeur de fiscalité active pour le comparateur.
   * - Si l'événement vient d'un contrôle tax_program (radio ou <select>), on
   *   prend sa value.
   * - Sinon : source de vérité = le radio `criterias[tax_program]` coché (#308).
   * - Replis legacy (anciens <select> #tax_program / #tax_program_ptz).
   */
  currentTaxProgramValue(changedControl = null) {
    if (changedControl instanceof HTMLSelectElement) return changedControl.value;
    if (
      changedControl instanceof HTMLInputElement &&
      changedControl.type === "radio" &&
      changedControl.checked
    ) {
      return changedControl.value;
    }

    // Groupe de radios unifié (#308).
    const checkedRadio = this.element.querySelector("input[name='criterias[tax_program]']:checked");
    if (checkedRadio) return checkedRadio.value;

    // Replis legacy (anciens sélecteurs).
    const mode = this.element.querySelector('input[type="radio"][name="profile[investment_mode]"]:checked');
    const selector = mode && mode.value === "residence" ? "#tax_program_ptz" : "#tax_program";
    const select = this.element.querySelector(selector);
    if (select instanceof HTMLSelectElement) return select.value;

    const enabledSelect = this.element.querySelector('select[data-toggle-scope="tax_program"]:not([disabled])');
    return enabledSelect instanceof HTMLSelectElement ? enabledSelect.value : null;
  }

  // Select a tax program when clicking on a fiscality card
  // Usage on clickable element:
  // data-action="click->invest-details#chooseTaxProgram"
  // data-tax-program="<key>"
  chooseTaxProgram(event) {
    try {
      const el = event.currentTarget;
      const taxProgram = el && (el.dataset.taxProgram || el.getAttribute("data-tax-program"));
      if (!taxProgram) return;

      // Do not touch the <select> (it would trigger listings_search as well).
      // Instead, force-refresh listing_details with a one-shot payload patch.
      try {
        this.element.dispatchEvent(new CustomEvent("api-sync:refresh", {
          bubbles: true,
          detail: {
            scope: "listing_details",
            force: true,
            payloadPatch: { criterias: { tax_program: taxProgram } },
          },
        }));
      } catch (_) {}

      // Update UI toggles (tax_program scope) without modifying the select value.
      try {
        this.element.dispatchEvent(new CustomEvent("toggle:set", {
          bubbles: true,
          detail: { scope: "tax_program", value: taxProgram },
        }));
      } catch (_) {}
    } catch (_) {
      // no-op
    }
  }
}